<template>
  <section>
    <h4 v-if="displayTitle" class="mb-4 font-weight-normal">
      {{ "sections.detailsSoftwareSection.title" | i18n }}
    </h4>
    <Loading v-if="!isReady" :type="3" />
    <div v-else class="w-100 h-100">
      <div class="mb-4" v-for="(info, index) in infos" :key="index">
        <SoftwareDetailsBloc :info="info" />
      </div>
      <b-row>
        <GenericFormUpdate class="pr-2" :objectId="+objectId" :modalId="1" />
        <GenericFormDelete
          v-if="computedCanDelete"
          :id="'delete-form-' + objectType + '-' + objectId"
          :deleteFunction="computedDeleteFunction"
          :objectId="+objectId"
          :modalId="'modal-' + objectType + '-' + objectId"
        />
      </b-row>
    </div>
  </section>
</template>
<script>
import { detailsEntries } from "@/general";
import {
  SoftwareDetailsBloc,
  GenericFormDelete,
  GenericFormUpdate,
  Loading
} from "@/components";
/**
 * @description Composante qui affiche les détails d'un objet spécifique
 * @author Samuel Dubé
 * @author Samuel Colassin
 * @author Kevin Dubé
 * @author Francine Jin
 * @author Alexandre Gallant
 * @vue-prop {String} objectId - L'id de l'objet que l'on désire les informations
 * @vue-prop {String} objectType - Le type de l'objet dont on désire afficher les détails
 * @vue-prop {Boolean} displayTitle - Un booléen qui définit si on veut afficher un titre à notre section ou non.
 * @vue-data {Array} infos - Contient tous les blocs d'informations à afficher
 * @vue-data {Object} displayedEntries - Contient l'objet servant de masque pour afficher l'information
 * @vue-data {Boolean} isReady - Si on est prêt à afficher la section
 * @vue-computed {Function} computedDeleteFunction - Retourne la fonction de suppression correspondant au type de l'objet
 * @vue-computed {Boolean} computedCanDelete - True si l'utilisateur est un superAdmin ou que la section ne concerne pas ClientDetails
 */
export default {
  components: {
    SoftwareDetailsBloc,
    GenericFormDelete,
    GenericFormUpdate,
    Loading
  },
  props: {
    objectId: {
      required: true,
      type: String
    },
    objectType: {
      required: true,
      type: String,
      validator: prop => ["software", "server", "client"].includes(prop)
    },
    displayTitle: {
      required: false,
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      infos: [],
      displayedEntries: detailsEntries,
      isReady: false
    };
  },
  computed: {
    computedDeleteFunction() {
      switch (this.objectType) {
        case "software":
          return this.$api.deleteSoftware;
        case "server":
          return this.$api.deleteServer;
        case "client":
          return this.$api.deleteClient;
        default:
          return new Error();
      }
    },
    computedCanDelete() {
      return this.$user.get().role === "admin" || this.objectType != "client";
    }
  },
  /**
   * @description Effectue la requête nécessaire pour charger les informations de l'objet voulu.
   */
  beforeMount() {
    this.getDetails();
  },
  methods: {
    /**
     * @description Obtient les détails de l'objet désiré, selon le type spécifié
     * @returns {Promise} Une promesse contenant les détails de l'objet
     * @throws {TypeError} Indique que le type de l'objet n'est pas supporté
     */
    async getDetails() {
      let result;
      let title = "sections.detailsSoftwareSection.details";
      let action = "sections.detailsSoftwareSection.modify";
      let icon = "edit";

      switch (this.objectType) {
        case "software":
          await this.$api.getSoftware(this.objectId).then(res => {
            result = this.getDetailsForObject(res, title, action, icon);
          });
          break;
        case "server":
          title = "sections.detailsSoftwareSection.serveur";
          action = "sections.detailsSoftwareSection.details";
          icon = "external link";
          await this.$api.getServer(this.objectId).then(res => {
            result = this.getDetailsForObject(res, title, action, icon);
          });
          break;
        case "client":
          await this.$api.getClient(this.objectId).then(res => {
            result = this.getDetailsForObject(res, title, action, icon);
          });
          break;
        default:
          throw new TypeError(
            "Unsupported objectType specified : " + this.objectType
          );
      }
      this.isReady = true;
      return result;
    },
    /**
     * @description Permet d'obtenir les détails d'un objet donné et de le rendre
     * propice à l'affichage
     * @param {Object} detailObject Le résultat de la requête de laquelle on veut extraire les données
     * @param {String} title Le titre à associer au résultat
     * @param {String} action L'action à associer au résultat
     * @param {String} icon L'icône à associer au résultat
     * @returns {Promise} Une promesse contenant les données
     * @throws {Error} Erreur lancée quand il n'y a pas de données
     */
    getDetailsForObject(detailObject, title, action, icon) {
      if (detailObject.data && detailObject.data.data) {
        let info = this.buildInfoObject(title, action, icon);
        info.entries = this.parseEntriesFromData(detailObject.data.data);
        this.infos.push(info);
        this.$emit("onDataFetched", this.infos[0]);
        return detailObject.data.data;
      } else {
        this.$emit("onDataFetched", this.infos[0]);
        throw new Error("No data received");
      }
    },
    /**
     * @description Méthode qui construit un objet d'information
     * @param {String} title Le titre à associer au résultat
     * @param {String} action L'action à associer au résultat
     * @param {String} icon L'icône à associer au résultat
     * @returns {Object} L'objet d'information
     */
    buildInfoObject(title, action, icon) {
      return {
        title: title,
        action: action,
        icon: icon,
        entries: []
      };
    },
    /**
     * @description Méthode qui construit un objet pour une entrée
     * @param {String} key Clé à assigner à l'entrée
     * @param {String|Number} value Texte à afficher pour l'entrée
     * @param {Function} transform Fonction à appeler pour obtenir le texte à ajouter au nom de l'entrée
     * @param {String} tooltip Texte à afficher
     * @param {String} group Groupe lié à l'entrée
     * @returns {Object} L'objet d'entrée construit
     */
    buildEntryObject(key, value, transform, tooltip, group) {
      return {
        lang: this.displayedEntries[key].lang,
        key: key,
        value: this.convertValue(value, transform),
        tooltip: tooltip,
        group: group
      };
    },
    /**
     * @description Méthode qui convertit les données recueillies en se basant
     * sur la propriété displayedEntry
     * @param {Object} data Données à convertir
     * @returns {Object} Un objet contenant les entrées converties
     */
    parseEntriesFromData(data) {
      let entries = {};

      for (let [key, value] of Object.entries(data)) {
        const specificKey =
          this.objectType === "client" ? "client_" + key : key;
        const displayedEntry = this.displayedEntries[specificKey];

        // S'il faut afficher l'entrée
        if (displayedEntry) {
          let entry = this.buildEntryObject(
            specificKey,
            value,
            displayedEntry.transform,
            displayedEntry.tooltip,
            displayedEntry.group
          );

          // Si l'entrée est un groupe, on créer un array
          if (displayedEntry.group) {
            entries[entry.group] = entries[entry.group] || [];
            entries[entry.group].push(entry);
          } else {
            // Sinon on assigne l'objet
            entries[entry.key] = entry;
          }
        }
      }

      return entries;
    },

    /**
     * @description Méthode qui convertit la valeur fournie en paramêtre
     * @param {String} value Valeur à convertir
     * @param {Function} transform Transformation à appliquer à la valeur
     * @returns {String} La valeur passée en paramètre convertie
     */
    convertValue(value, transform) {
      if (typeof value === "boolean") {
        return this.$i18n.t(`common.${value}`);
      }
      return transform ? transform(value) : value;
    }
  }
};
</script>
